import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Context as AISearchContext } from './context';
import { firstPrompt, formatChatTime, groupChats } from './history';
import {
  HistoryPane,
  HistorySearch,
  HistoryScroll,
  HistoryGroupLabel,
  HistoryRow,
  HistoryOpen,
  HistoryPrompt,
  HistoryMeta,
  HistoryDelete,
  HistoryEmpty
} from './styles';

interface Props {
  /** Called after a chat is picked, so the drawer can return to the conversation. */
  onSelect: () => void;
  /** Called on Escape, to leave the history without picking a chat. */
  onClose: () => void;
}

export const ChatHistory: React.FC<Props> = ({ onSelect, onClose }) => {
  const { chats, chatsLoaded, activeChatId, switchChat, deleteChat } = useContext(AISearchContext);
  const [query, setQuery] = useState('');
  const paneRef = useRef<HTMLDivElement>(null);

  // Move focus into the pane (so Escape works) without opening the on-screen
  // keyboard the way focusing the search box would.
  useEffect(() => {
    paneRef.current?.focus();
  }, []);

  const groups = useMemo(() => groupChats(chats, query), [chats, query]);
  const hasPrompts = useMemo(() => chats.some((chat) => firstPrompt(chat) !== ''), [chats]);

  const handleSelect = (chatId: string) => {
    switchChat(chatId);
    onSelect();
  };

  return (
    <HistoryPane
      id="ai-chat-history"
      ref={paneRef}
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <HistorySearch
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search your past prompts"
        aria-label="Search your past prompts"
      />
      <HistoryScroll>
        {groups.length === 0 && (
          <HistoryEmpty>
            {!chatsLoaded ? 'Loading your past prompts…' : hasPrompts ? 'No prompts match your search.' : 'No past prompts yet. Ask the assistant something and it will show up here.'}
          </HistoryEmpty>
        )}
        {groups.map((group) => (
          <section key={group.label} aria-label={group.label}>
            <HistoryGroupLabel>{group.label}</HistoryGroupLabel>
            {group.chats.map((chat) => {
              const prompt = firstPrompt(chat);
              const count = chat.messages.length;
              return (
                <HistoryRow key={chat.id} $active={chat.id === activeChatId}>
                  <HistoryOpen
                    type="button"
                    onClick={() => handleSelect(chat.id)}
                    title={prompt}
                    aria-current={chat.id === activeChatId ? 'true' : undefined}
                  >
                    <HistoryPrompt>{prompt}</HistoryPrompt>
                    <HistoryMeta>
                      {formatChatTime(chat.updatedAt)} · {count} {count === 1 ? 'message' : 'messages'}
                    </HistoryMeta>
                  </HistoryOpen>
                  <HistoryDelete
                    type="button"
                    onClick={() => deleteChat(chat.id)}
                    aria-label={`Delete chat: ${prompt.slice(0, 60)}`}
                    title="Delete chat"
                  >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </HistoryDelete>
                </HistoryRow>
              );
            })}
          </section>
        ))}
      </HistoryScroll>
    </HistoryPane>
  );
};
